"use client";

import { useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import type { HTMLAttributes } from "react";
import { FilterBadge } from "@/components/filters/badges";
import type { FilterType } from "@/components/filters/types";
import { badgeVariants } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { sortingData } from "@/features/diets/data/sorting";
import { serializeTagsClient } from "@/features/diets/helpers/serialize-tags";
import { defaultLocale } from "@/i18n/defaults";
import { usePathname, useRouter } from "@/i18n/navigation";
import {
  flattenSearchParams,
  type ParamObject,
  parseSearchParams,
  removeSearchParam,
} from "@/lib/utilities";
import { cn } from "@/lib/utils";
import type { ApiPlatformResponseType } from "@/types/api-platform.type";
import type { TagsResponse } from "../types/tags.type";

const availableFilters = [
  "search",
  "tags",
  "sortOption",
  "priceFrom",
  "priceTo",
  "price[gte]",
  "price[lte]",
  "promoPrice[gte]",
  "promoPrice[lte]",
  "menuSelection",
  "numberOfMeals",
  "caloricity",
  "tagsSlugs",
  "preset",
];

const priceFilters = [
  "priceFrom",
  "priceTo",
  "price[gte]",
  "price[lte]",
  "promoPrice[gte]",
  "promoPrice[lte]",
];

type FilterCurrentProps = HTMLAttributes<HTMLDivElement> & {
  tags?: ApiPlatformResponseType<TagsResponse>;
};

export const FilterCurrent = ({
  className,
  tags,
  ...props
}: FilterCurrentProps) => {
  const t = useTranslations();
  const searchParams = useSearchParams();
  const parsedParams = parseSearchParams(searchParams);
  const { replace } = useRouter();
  const serializedTags = serializeTagsClient(tags?.["hydra:member"]);

  // On dynamic routes (/caterings/[slug]) the i18n usePathname can yield a template path.
  const templatePathname = usePathname();
  const currentPathname =
    typeof window !== "undefined" && window.location?.pathname
      ? window.location.pathname
      : templatePathname;

  const getValue = (param: ParamObject) => {
    if (param.name === "sortOption") {
      const label = sortingData.find(
        (option) => option.value === param.value,
      )?.label;
      return t("diets.filters.sort." + label);
    }
    return param.value === "true" ? "" : param.value;
  };

  const flattenParams = flattenSearchParams(parsedParams);

  const extendedParams: FilterType[] = flattenParams
    .filter((param) => availableFilters.includes(param.name))
    .filter((param) => param.name !== "page")
    .map((param) => {
      if (tags && (param.name === "tags" || param.name === "tagsSlugs")) {
        return {
          label:
            serializedTags?.find((tag) => tag.slug === param.value)?.label ||
            "",
          name: param.name,
          value: getValue(param),
          type: "no_value",
        };
      }

      if (param.name === "preset") {
        const numberOfMeals = t(`filters.presets.${param.value}.numberOfMeals`);
        return {
          label: `
            ${t(`filters.presets.${param.value}.title`)}
            (${numberOfMeals}  ${t("common.meals", { meals: parseInt(numberOfMeals) }).replace(/^\d+\s*/, "")})`,
          name: param.name,
          value: getValue(param),
          type: "no_value",
        };
      }

      if (tags && priceFilters.includes(param.name)) {
        return {
          label: t("filters.badges." + param.name),
          name: param.name,
          suffix: " " + t("common.currencies." + defaultLocale),
          value: getValue(param),
          type: param.value === "true" ? "true_false" : "multi",
        };
      }

      return {
        label: t("filters.badges." + param.name),
        name: param.name,
        value: getValue(param),
        type: param.value === "true" ? "true_false" : "multi",
      };
    });

  const handleRemove = (filter: FilterType) => () => {
    const params = new URLSearchParams(searchParams);
    params.delete("page");
    const newParams = removeSearchParam(params, filter);

    replace(`${currentPathname}?${newParams.toString()}`, {
      scroll: false,
    });
  };

  const handleClear = () => {
    replace(currentPathname, { scroll: false });
  };

  return extendedParams.length > 0 ? (
    <div className={cn("flex-wrap gap-2 md:flex", className)} {...props}>
      <Carousel opts={{ align: "start", dragFree: true }}>
        <CarouselContent className="-ml-2 max-md:last:mr-12">
          {extendedParams.map((filter) => (
            <CarouselItem
              key={`${filter.name}-${filter.type}-${filter.value}`}
              className="basis-auto pl-2"
            >
              <FilterBadge filter={filter} removeFilter={handleRemove} />
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
      {extendedParams.length > 1 && (
        <Button
          className={cn(
            badgeVariants({ variant: "outline" }),
            "h-[34px] min-h-7 cursor-pointer rounded-md border-0 px-2 py-3 text-brand-green1-300 text-sm underline underline-offset-4",
          )}
          onClick={handleClear}
          variant="outline"
        >
          <div className="font-medium">{t("filters.badges.clear")}</div>
        </Button>
      )}
    </div>
  ) : null;
};
